/**
 * Markdown pipe tables in bot answers (e.g. Versi fee lists: "who hasn't paid October fee").
 *
 * The backend sends a lead sentence, a blank line, then a standard pipe table — header row,
 * `| --- |` separator row, data rows — sometimes followed by one more line of prose. The answer is
 * split into text and table segments so the text keeps the normal markdown rendering and each
 * table is drawn by `AttendanceRecordsTable`, exactly like the attendance answers.
 */
import {markdownToPlainLines} from '../../modes/attendance/attendanceExportFigures'

export type MarkdownTableSegment = {
  kind: 'table'
  columns: string[]
  rows: Record<string, string>[]
}
export type MarkdownSegment = {kind: 'text'; text: string} | MarkdownTableSegment

/** A line that is part of a table: starts and ends with `|`. */
const isTableLine = (line: string): boolean => {
  const trimmed = line.trim()
  return trimmed.length > 1 && trimmed.startsWith('|') && trimmed.endsWith('|')
}

/** The header separator row: `| --- | :---: | ---: |`. */
const SEPARATOR_ROW = /^\|(\s*:?-{3,}:?\s*\|)+$/

/** Cells of a row, trimmed; `\|` stays a literal pipe inside a cell. */
const splitRow = (line: string): string[] =>
  line
    .trim()
    .slice(1, -1)
    .split(/(?<!\\)\|/)
    .map((cell) => cell.replace(/\\\|/g, '|').trim())

/** Column names as row keys must be unique; a repeated header gets " (2)", " (3)", … */
const uniqueColumns = (header: string[]): string[] => {
  const seen = new Map<string, number>()
  return header.map((name, i) => {
    const base = name || `Column ${i + 1}`
    const count = (seen.get(base) ?? 0) + 1
    seen.set(base, count)
    return count === 1 ? base : `${base} (${count})`
  })
}

const toTable = (header: string[], body: string[][]): MarkdownTableSegment => {
  const columns = uniqueColumns(header)
  // Rows can be short or long; every row gets exactly the header's columns.
  const rows = body.map((cells) =>
    Object.fromEntries(columns.map((column, i) => [column, cells[i] ?? '']))
  )
  return {kind: 'table', columns, rows}
}

/**
 * Split an answer into text and table segments, in order. A table is a header line, a
 * separator line, then any further table lines. Text segments are passed through untouched.
 */
export const splitMarkdownTables = (text: string): MarkdownSegment[] => {
  const lines = text.split('\n')
  const segments: MarkdownSegment[] = []
  let prose: string[] = []
  const flushProse = () => {
    if (prose.join('').trim()) segments.push({kind: 'text', text: prose.join('\n')})
    prose = []
  }
  for (let i = 0; i < lines.length; i++) {
    const next = lines[i + 1]
    if (isTableLine(lines[i]) && next !== undefined && SEPARATOR_ROW.test(next.trim())) {
      flushProse()
      const header = splitRow(lines[i])
      const body: string[][] = []
      let j = i + 2
      for (; j < lines.length && isTableLine(lines[j]); j++) body.push(splitRow(lines[j]))
      segments.push(toTable(header, body))
      i = j - 1
    } else {
      prose.push(lines[i])
    }
  }
  flushProse()
  return segments
}

/** A list item line as `markdownToPlainLines` leaves it: "1. …", "1) …" or "• …". */
const LIST_ITEM_LINE = /^(?:\d+[.)]|•)\s/

/**
 * The answer's text as export lines, minus the list items that only repeat the table's rows
 * ("1. Admission Fee: PKR 10,000, assigned to 1 class…") — the same idea as
 * `condenseAttendanceNarrative` for attendance exports. Falls back to every line rather than
 * print nothing.
 */
export const exportTextLines = (markdown: string): string[] => {
  const lines = markdownToPlainLines(markdown)
  const prose = lines.filter((line) => !LIST_ITEM_LINE.test(line))
  return prose.length ? prose : lines
}

/** A value that reads as a name rather than an ID, amount or date: it starts with a letter. */
const STARTS_WITH_LETTER = /^\p{L}/u

/**
 * Rows ordered A–Z by the first name-like column — the first whose filled cells all start with a
 * letter ("Student", "Name"), so a leading ID or amount column is skipped. Rows with that cell
 * empty go last; rows keep their incoming order on ties. No such column → rows unchanged.
 */
export const sortRowsAlphabetically = (
  columns: string[],
  rows: Record<string, string>[]
): Record<string, string>[] => {
  const sortColumn = columns.find((column) => {
    const values = rows.map((row) => row[column]?.trim() ?? '').filter(Boolean)
    return values.length > 0 && values.every((value) => STARTS_WITH_LETTER.test(value))
  })
  if (!sortColumn) return rows
  const collator = new Intl.Collator(undefined, {sensitivity: 'base', numeric: true})
  return [...rows].sort((a, b) => {
    const left = a[sortColumn]?.trim() ?? ''
    const right = b[sortColumn]?.trim() ?? ''
    if (!left || !right) return Number(!left) - Number(!right)
    return collator.compare(left, right)
  })
}

/**
 * Export title (i18n key) and file name for a table answer, from the backend's `capability`.
 * Fee answers (`fee.data`, …) export as "Fee report" / `fee-data`, the way attendance exports as
 * "Attendance report" / `attendance-<intent>`; anything else is a "Versi report" / `versi-table`.
 */
export const exportNamingFor = (
  capability: string | null | undefined
): {titleId: string; filename: string} => {
  const id = capability?.trim().toLowerCase() || ''
  if (id === 'fee' || id.startsWith('fee.')) {
    return {
      titleId: 'CHATBOT.ASSISTANT.EXPORT_FEE_REPORT_TITLE',
      filename: id.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''),
    }
  }
  return {titleId: 'CHATBOT.ASSISTANT.EXPORT_REPORT_TITLE', filename: 'versi-table'}
}
